import { Alert, Box, Button, IconButton, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState, type ReactNode } from 'react';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { DataTable } from './DataTable/DataTable';
import { PageHeader } from './PageHeader';
import { ConfirmDialog } from './Dialogs/ConfirmDialog';
import { FormDialog } from './Dialogs/FormDialog';
import { useCrudResource } from '../hooks/useCrudResource';
import type { CrudService } from '../../core/services/crud.service';
import { extractErrorMessage } from '../../core/services/api';

export interface CrudFormProps<T> {
  values: Partial<T>;
  setValues: (next: Partial<T>) => void;
}

interface CrudPageProps<T extends { id: number }> {
  title: string;
  description?: string;
  cacheKey: readonly unknown[];
  service: CrudService<T, Partial<T>, Partial<T>>;
  /** Relation names to eager-load on the list request (e.g. `['tenant']`). */
  include?: string[];
  columns: GridColDef[];
  renderForm: (props: CrudFormProps<T>) => ReactNode;
  defaultValues?: Partial<T>;
  /** Hook to mutate the payload before sending (e.g. inject FKs from URL). */
  beforeSubmit?: (values: Partial<T>, mode: 'create' | 'edit') => Partial<T>;
  headerActions?: ReactNode;
  /**
   * Singular form used in dialog titles. Falls back to stripping a trailing
   * 's' from `title`; supply this when the auto-singular looks wrong.
   */
  entityName?: string;
  /** Extra per-row icons rendered to the left of the built-in edit/delete. */
  rowActions?: (row: T) => ReactNode;
  actionsColumnWidth?: number;
}

/**
 * Strip server-managed fields that must never be sent in a create/update
 * payload. The backend rejects them (or silently duplicates tenant scoping).
 */
function sanitizePayload<T>(values: Partial<T>): Partial<T> {
  const { id, createdAt, updatedAt, tenantId, tenant_id, created_at, updated_at, ...rest } =
    values as Record<string, unknown>;
  void id; void createdAt; void updatedAt; void tenantId; void tenant_id;
  void created_at; void updated_at;
  return rest as Partial<T>;
}

/**
 * One-stop CRUD scaffolding for list pages. Handles pagination, the create/
 * edit dialog, and the delete confirmation. For master/detail or custom
 * panels, drop down to the underlying primitives (DataTable + dialogs).
 */
export function CrudPage<T extends { id: number }>({
  title,
  description,
  cacheKey,
  service,
  include,
  columns,
  renderForm,
  defaultValues,
  beforeSubmit,
  headerActions,
  entityName,
  rowActions,
  actionsColumnWidth,
}: CrudPageProps<T>) {
  const singular = entityName ?? title.replace(/s$/, '');
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const { list, create, update, remove } = useCrudResource<T>({
    key: cacheKey,
    service,
    query: { page: pagination.page + 1, pageSize: pagination.pageSize, sort: '-id', include },
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<T | null>(null);
  const [values, setValues] = useState<Partial<T>>(defaultValues ?? {});
  const [confirmDelete, setConfirmDelete] = useState<T | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const openCreate = () => {
    setEditing(null);
    setValues(defaultValues ?? {});
    setSubmitError(null);
    setDialogOpen(true);
  };

  const openEdit = (row: T) => {
    setEditing(row);
    setValues(row);
    setSubmitError(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const mode = editing ? 'edit' : 'create';
    const sanitized = sanitizePayload(values);
    const payload = beforeSubmit ? beforeSubmit(sanitized, mode) : sanitized;
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, payload });
      } else {
        await create.mutateAsync(payload);
      }
      setDialogOpen(false);
    } catch (err) {
      setSubmitError(extractErrorMessage(err));
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    await remove.mutateAsync(confirmDelete.id);
    setConfirmDelete(null);
  };

  const fullColumns: GridColDef[] = [
    ...columns,
    {
      field: '__actions',
      headerName: '',
      sortable: false,
      filterable: false,
      width: actionsColumnWidth ?? (rowActions ? 150 : 110),
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ height: '100%' }}
        >
          {rowActions?.(params.row as T)}
          <IconButton size="small" onClick={() => openEdit(params.row as T)} aria-label="edit">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => setConfirmDelete(params.row as T)}
            aria-label="delete"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader
        title={title}
        description={description}
        actions={
          <>
            {headerActions}
            <Button startIcon={<AddIcon />} variant="contained" onClick={openCreate}>
              New
            </Button>
          </>
        }
      />

      <DataTable<T>
        rows={list.data?.data ?? []}
        columns={fullColumns}
        loading={list.isFetching}
        rowCount={list.data?.page.total ?? 0}
        paginationMode="server"
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        errorMessage={list.isError ? extractErrorMessage(list.error) : undefined}
      />

      <FormDialog
        open={dialogOpen}
        title={editing ? `Edit ${singular}` : `New ${singular}`}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        loading={create.isPending || update.isPending}
        submitLabel={editing ? 'Save changes' : 'Create'}
      >
        {submitError && <Alert severity="error">{submitError}</Alert>}
        {renderForm({ values, setValues })}
      </FormDialog>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete"
        message={`Delete "${
          (confirmDelete as { name?: string } | null)?.name ?? `record #${confirmDelete?.id}`
        }"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={handleDelete}
        onClose={() => setConfirmDelete(null)}
      />
    </Box>
  );
}
